import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import styled from '@emotion/styled';
import { LanguageIcon } from '@seedlang/icons';
import { trackMeaningExpansion } from '@seedlang/hoc';
import { isPresent, pixify } from '@seedlang/utils';
import Text from 'components/text';
import RelatedWords from 'components/vocab/related_words';
import WordCardRow from 'components/word_card/word_card_row';
import TranslationWithSource from 'components/translation_with_source';

const Wrapper = styled.div`
  width: 100%;
`;

const HeaderWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const RegisterLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-left: 12px;
  margin-right: 8px;
`;

const MainContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  ${props => props.expanded && `
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    margin-bottom: -6px;
  `}
`;

const ExpandedContent = styled.div`
  margin: 5px 5px 5px 0;
  padding: 5px 10px 5px 5px;
  border-radius: 5px;
`;

const ListItem = styled.div`
  margin-bottom: 10px;
  text-align: left;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SentenceListItem = styled.div`
  margin-bottom: 0;
  text-align: left;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ListItemText = styled.div`
  font-size: ${props => props.fontSize};
  color: #000;
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
`;


@observer
class Meaning extends React.Component {
  @observable expanded = false;

  static defaultProps = {
    fontSize: '16px',
    lineHeight: '22px',
  };
  toggleExpanded = () => {
    if (!this.isExpandable) {
      return;
    }
    this.expanded = !this.expanded;
    if (this.expanded) {
      trackMeaningExpansion(this.props.word, this.props.meaning);
    }
  }
  onClickSynonym = (synonym) => {
    if (this.props.onClickWord) {
      this.props.onClickWord(synonym);
    }
  }
  getSourceText = (translationSource) => {
    const sourceText = this.props.user && translationSource.source?.textForUser
      ? translationSource.source.textForUser(this.props.user)
      : translationSource.source?.text;
    if (!sourceText) return null;
    if (translationSource.source?.displayGender && this.props.word?.genderAbbreviation) {
      return `${sourceText}${this.props.word.genderAbbreviation}`;
    }
    return sourceText;
  }

  sentenceSourcesForUser = (sentence) => {
    const sources = sentence.sources || [];
    const userLanguageId = this.props.user?.languageId || 'EN';
    const prefersBritishEnglish = userLanguageId === 'EN2' || (userLanguageId === 'EN' && this.props.user?.useBritishEnglish);
    const preferredLanguageId = prefersBritishEnglish ? 'EN2' : userLanguageId;
    const sourcesForLanguage = languageId => sources.filter(source => source.languageId === languageId);
    const englishLanguageIds = prefersBritishEnglish ? ['EN2', 'EN'] : ['EN', 'EN2'];
    const englishSources = englishLanguageIds.reduce((accumulator, languageId) => {
      return accumulator.concat(sourcesForLanguage(languageId));
    }, []);
    if (userLanguageId.startsWith('EN')) {
      const preferredSources = sourcesForLanguage(preferredLanguageId);
      if (isPresent(preferredSources)) {
        return preferredSources;
      }
      const fallbackLanguageId = preferredLanguageId === 'EN2' ? 'EN' : 'EN2';
      const fallbackSources = sourcesForLanguage(fallbackLanguageId);
      if (isPresent(fallbackSources)) {
        return fallbackSources;
      }
      return sources;
    }
    const nativeSources = sourcesForLanguage(preferredLanguageId);
    if (this.props.user?.onlyShowNativeLanguage) {
      if (isPresent(nativeSources)) {
        return nativeSources;
      }
      if (isPresent(englishSources)) {
        return englishSources;
      }
      return sources;
    }
    const selectedSources = nativeSources.concat(englishSources);
    if (isPresent(selectedSources)) {
      return selectedSources;
    }
    return sources;
  }

  @computed get translationSources() {
    const translationSources = this.props.meaning?.translationSourcesForUser(this.props.user) || [];
    const userLanguageId = this.props.user?.languageId || 'EN';
    if (userLanguageId.startsWith('EN')) {
      return translationSources;
    }
    const nativeSources = [];
    const englishSources = [];
    translationSources.forEach(translationSource => {
      if (translationSource.source?.languageId?.startsWith('EN')) {
        englishSources.push(translationSource);
      } else {
        nativeSources.push(translationSource);
      }
    });
    return nativeSources.concat(englishSources);
  }

  @computed get isExpandable() {
    return this.translationSources?.length > 1 || (this.props.meaning?.sentences?.length > 0);
  }

  @computed get displaySynonyms() {
    const synonyms = this.props.meaning?.synonyms || [];
    if (!this.props.synonymOverride) {
      return synonyms;
    }
    return [
      this.props.synonymOverride,
      ...synonyms.filter(synonym => String(synonym.id) !== String(this.props.synonymOverride.id)),
    ];
  }

  @computed get meaningSentences() {
    return (this.props.meaning?.sentences || [])
      .map((sentence, index) => ({ sentence, index }))
      .sort((itemA, itemB) => {
        const sentenceATextLength = (itemA.sentence?.targetText || itemA.sentence?.target_text || '').length;
        const sentenceBTextLength = (itemB.sentence?.targetText || itemB.sentence?.target_text || '').length;
        if (sentenceATextLength !== sentenceBTextLength) {
          return sentenceATextLength - sentenceBTextLength;
        }
        return itemA.index - itemB.index;
      })
      .map(item => item.sentence)
      .map(sentence => {
        const preferredSource = this.sentenceSourcesForUser(sentence)[0];
        return Object.assign(Object.create(sentence), {
          languageId: this.props.word?.languageId,
          sourceText: preferredSource?.text,
          sourceTextForUser: user => {
            if (!preferredSource) {
              return null;
            }
            if (preferredSource.textForUser) {
              return preferredSource.textForUser(user);
            }
            return preferredSource.text;
          },
          sourceTextForUserLanguageId: () => preferredSource?.languageId,
        });
      });
  }

  render() {
    return (
      <Wrapper>
        <WordCardRow
          id={this.props.meaning?.id}
          expandedId={this.expanded ? this.props.meaning?.id : null}
          keepExpanded={!this.isExpandable}
          onClick={this.toggleExpanded}
          rowMargin={this.props.rowMargin}
        >
          <HeaderWrapper>
            <ContentWrapper>
              <MainContent expanded={this.expanded}>
                {
                  this.expanded ?
                    <>
                      {
                        this.translationSources?.map(translationSource => {
                          const sourceText = this.getSourceText(translationSource);
                          if (!sourceText) return null;
                          return (
                            <ListItem key={translationSource.id}>
                              <ListItemText fontSize={this.props.fontSize}>
                                <LanguageIcon
                                  margin='0 5px 0 0'
                                  languageId={translationSource.source?.languageId}
                                />
                                {sourceText}
                              </ListItemText>
                            </ListItem>
                          );
                        })
                      }
                      {
                        this.displaySynonyms.length > 0 &&
                          <ListItem>
                            <ListItemText fontSize={this.props.fontSize}>
                              <LanguageIcon
                                margin='0 5px 0 0'
                                languageId={this.props.word.languageId}
                              />
                              <RelatedWords
                                expanded
                                referenceWord={this.props.word}
                                words={this.displaySynonyms}
                                onClickWord={synonym => {
                                  this.onClickSynonym(synonym);
                                }}
                                margin="0"
                              />
                            </ListItemText>
                          </ListItem>
                      }
                    </>
                    :
                    <>
                      {
                        this.translationSources?.length > 0 &&
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <LanguageIcon
                              margin='0 5px 0 0'
                              languageId={this.translationSources[0].source?.languageId}
                            />
                            <Text
                              fontSize={pixify(this.props.fontSize)}
                              lineHeight={pixify(this.props.lineHeight)}
                              textAlign='left'
                            >
                              {this.getSourceText(this.translationSources[0])}
                            </Text>
                          </div>
                      }
                      {
                        this.displaySynonyms.length > 0 &&
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <LanguageIcon
                              margin='0 5px 0 0'
                              languageId={this.props.word.languageId}
                            />
                            <Text
                              fontSize={pixify(this.props.fontSize)}
                              lineHeight={pixify(this.props.lineHeight)}
                              textAlign='left'
                            >
                              <span
                                onClick={e => {
                                  e.stopPropagation();
                                  this.onClickSynonym(this.displaySynonyms[0]);
                                }}
                                style={{ borderBottom: '2px solid #b5b5b5', cursor: 'pointer' }}
                              >
                                {this.displaySynonyms[0].targetTextWithDefiniteArticle}
                              </span>
                            </Text>
                          </div>
                      }
                    </>
                }
              </MainContent>
            </ContentWrapper>
            {
              isPresent(this.props.meaning.register) &&
                <RegisterLabel>
                  {this.props.meaning.register}
                </RegisterLabel>
            }
          </HeaderWrapper>
          {
            this.expanded &&
              <ExpandedContent>
                {
                  this.meaningSentences.length > 0 && this.meaningSentences.map(sentence => (
                    <SentenceListItem key={sentence.id}>
                      <TranslationWithSource
                        background='transparent'
                        languageButtonBackground='#FFF'
                        translation={sentence}
                        padding='0 0 14px 0'
                        fontSize={pixify(this.props.fontSize)}
                        languageButtonWrapperWidth='25px'
                        languageButtonWrapperPadding='0 5px 0 0'
                        showSentenceBreakdown
                        sentenceBreakdownProps={{
                          highlightWordId: this.props.word?.id,
                          hideWordAssociationHighlight: true,
                          disableOnClick: true,
                        }}
                        user={this.props.user}
                      />
                    </SentenceListItem>
                  ))
                }
              </ExpandedContent>
          }
        </WordCardRow>
      </Wrapper>
    );
  }
}

export default Meaning;
